import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { isNativeApp } from 'common/webview';
import { isLoggedIn } from 'common/utils/auth';
import { calculateSettlement } from 'common/utils/settlementCalculator';
import {
    AppScreen, Button, ButtonFooter, FieldGroup, InfoGrid, PageIntro,
    Section, SectionHeader, TextField,
} from 'components/ds';
import styles from './CostPublic.module.css';
import { formatPhone } from 'common/utils/format';

/**
 * 공개 정산 결과 페이지 (인증 불필요)
 * - 딥링크/공유 링크로 진입
 * - 앱 사용자: 바로 정산 결과 표시
 * - 비회원: 이름+전화번호 확인 후 열람
 */
export const CostPublic = () => {
    const { t } = useTranslation();
    const { meetingId } = useParams();
    const [payments, setPayments] = useState<any[]>([]);
    const [meetingName, setMeetingName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');

    const inApp = isNativeApp();
    const loggedIn = isLoggedIn();

    // 앱에서 로그인 상태면 바로 인증 통과
    useEffect(() => {
        if (inApp && loggedIn) setIsVerified(true);
    }, [inApp, loggedIn]);

    // 딥링크로 앱 열기 시도 (모바일 브라우저에서만)
    useEffect(() => {
        if (!meetingId || inApp) return;
        // 모바일 UA 가 아니면 시도하지 않음 (데스크톱에서 ERR_ABORTED 콘솔 에러 방지)
        const ua = navigator.userAgent || '';
        const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
        if (!isMobile) return;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = `dokocrew://cost/result/${meetingId}`;
        document.body.appendChild(iframe);
        const timer = setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 2000);
        return () => { clearTimeout(timer); if (iframe.parentNode) iframe.parentNode.removeChild(iframe); };
    }, [meetingId, inApp]);

    // 정산 데이터 조회
    useEffect(() => {
        if (!meetingId || !isVerified) return;
        const fetchData = async () => {
            try {
                const res = await instance.get(`${getApiUrl('meetings')}/${meetingId}/payments/public`);
                if (res?.data?.data) {
                    setPayments(res.data.data.map((p: any) => ({
                        ...p,
                        totalAmount: p.totalAmount ?? p.amount ?? 0,
                        paidByNickname: p.paidByNickname ? decodeURIComponent(p.paidByNickname) : p.paidByNickname,
                        splits: (p.splits || []).map((s: any) => ({
                            ...s,
                            amount: s.amount ?? s.splitAmount ?? 0,
                            nickname: s.nickname ? decodeURIComponent(s.nickname) : s.nickname,
                        })),
                    })));
                }
                // 모임명 조회
                try {
                    const meetingRes = await instance.get(`${getApiUrl('meetings')}/${meetingId}`);
                    setMeetingName(meetingRes?.data?.data?.meetingName || '모임');
                } catch {}
            } catch {
                setError('정산 정보를 불러올 수 없습니다.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [meetingId, isVerified]);

    const settlement = useMemo(() => calculateSettlement(payments), [payments]);


    const handleVerify = () => {
        if (!name.trim() || phone.replace(/[^0-9]/g, '').length < 10) return;
        // 간단한 본인 확인 — 이름만 체크 (서버에서 상세 검증 필요 시 API 추가)
        setIsVerified(true);
    };

    const won = (n: number) => t('home.wonAmount', { amount: n.toLocaleString() });

    /* 1) 본인 확인 */
    if (!isVerified) {
        return (
            <AppScreen bar="none" hasFooter>
                <PageIntro
                    title={t('costPublic.verifyTitle')}
                    desc={<>{t('costPublic.verifyDesc1')} {t('costPublic.verifyDesc2')}</>}
                />
                <Section spacing="tight">
                    <FieldGroup>
                        <TextField
                            id="verify-name"
                            label={t('guestLogin.name')}
                            required
                            type="text"
                            placeholder={t('costPublic.namePlaceholder')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={20}
                        />
                        <TextField
                            id="verify-phone"
                            label={t('guestLogin.phone')}
                            required
                            type="tel"
                            inputMode="numeric"
                            numeric
                            placeholder="010-0000-0000"
                            value={phone}
                            onChange={(e) => setPhone(formatPhone(e.target.value))}
                            maxLength={13}
                        />
                    </FieldGroup>
                </Section>
                <ButtonFooter>
                    <Button
                        variant="primary"
                        size="lg"
                        block
                        onClick={handleVerify}
                        disabled={!name.trim() || phone.replace(/[^0-9]/g, '').length < 10}
                    >
                        {t('common.confirm')}
                    </Button>
                </ButtonFooter>
            </AppScreen>
        );
    }

    if (isLoading) {
        return (
            <AppScreen bar="none">
                <p className={styles.state}>{t('costPublic.loading')}</p>
            </AppScreen>
        );
    }

    if (error) {
        return (
            <AppScreen bar="none">
                <p className={styles.state}>{error}</p>
            </AppScreen>
        );
    }

    /* 2) 정산 결과 */
    return (
        <AppScreen bar="none">
            <PageIntro title={meetingName} desc={t('costPublic.resultSub')} />

            <Section spacing="tight">
                <InfoGrid
                    columns={1}
                    items={[
                        { label: t('costComp.totalSpent'), value: won(settlement.totalAmount) },
                        { label: t('costComp.perPerson'), value: won(settlement.perPersonAmount) },
                        { label: t('costPublic.participants'), value: t('home.members', { count: settlement.participantCount }) },
                    ]}
                />
            </Section>

            {settlement.expenses.length > 0 && (
                <Section divided>
                    <SectionHeader title={t('costComp.expenseDetail')} count={settlement.expenses.length} />
                    <ul className={styles.rows}>
                        {settlement.expenses.map((e: any, i: number) => (
                            <li key={i} className={styles.row}>
                                <span className={styles.rowBody}>
                                    <span className={styles.rowTitle}>{e.description}</span>
                                    <span className={styles.rowMeta}>{t('costPublic.paidBy', { who: e.paidBy })}</span>
                                </span>
                                <span className={styles.rowAmount}>{won(e.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {settlement.transactions.length > 0 && (
                <Section divided>
                    <SectionHeader title={t('costComp.settlementDetail')} count={settlement.transactions.length} />
                    <ul className={styles.rows}>
                        {settlement.transactions.map((tx: any, i: number) => (
                            <li key={i} className={styles.row}>
                                <span className={styles.rowWho}>{tx.from}</span>
                                <span className={styles.rowArrow} aria-hidden>→</span>
                                <span className={styles.rowWho}>{tx.to}</span>
                                <span className={styles.rowAmount}>{won(tx.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* 앱에서 보기 — 브라우저에서만 */}
            {!inApp && (
                <Section divided last>
                    <p className={styles.promo}>{t('costPublic.appPromo')}</p>
                    <Button
                        variant="secondary"
                        size="lg"
                        block
                        onClick={() => { window.location.href = `dokocrew://cost/result/${meetingId}`; }}
                    >
                        {t('costPublic.openInApp')}
                    </Button>
                </Section>
            )}
        </AppScreen>
    );
};
